/**
 * WHAT IS IN FRONT OF WHAT, on a sea seen from above.
 *
 * It is two functions and it is in its own file for one reason: `world-view.ts` cannot be imported
 * by a test. It pulls in PixiJS and `import.meta.glob` over every baked PNG, so importing it costs
 * sixteen seconds in a node environment and needs a DOM it does not have. That is exactly why the
 * renderer had no test file at all, and exactly how a boat with no depth test lived in it for five
 * rounds. The pure part now lives where a fence can reach it in milliseconds, and the CALL SITE is
 * fenced by a source scan in `world-view.test.ts`.
 */

/**
 * The row below the lowest opaque row of a mask: where the thing stands on the water. `h` when the
 * mask is empty, which keeps an all-transparent sprite behind everything that has a silhouette.
 */
export function spriteFootPx(mask: Uint8Array, w: number, h: number): number {
  for (let y = h - 1; y >= 0; y--) {
    const row = y * w;
    for (let x = 0; x < w; x++) if (mask[row + x] === 1) return y + 1;
  }
  return h;
}

/** Anything the sprite layer paints, with the screen row it stands on. */
export interface DepthItem {
  readonly id: string;
  readonly feetPx: number;
}

/**
 * THE PAINT ORDER: nearest the camera last, which on this projection is lowest on the screen last.
 *
 * -- THE DEFECT THIS FUNCTION EXISTS TO END ------------------------------------
 *
 * The boat used to be appended to the sprite layer AFTER the island loop, unconditionally, with no
 * depth test at all. MEASURED in a real browser at grain 1 and grain 2: an island whose feet were
 * 73 art px NEARER the camera than the boat's still left all 4,084 hull pixels untouched, because
 * the hull went on top of it whatever the geometry said. CONTRACT 1.5n's last paragraph files it
 * as surviving any collision decision, and it does: a boat passing behind a headland drew in front
 * of it whether or not it was allowed to be there.
 *
 * The fix is not a new rule. It is the rule the islands already used, applied to the boat as well,
 * which is why this is a function rather than three lines inline - `world-view.test.ts` asserts the
 * boat is inside the ordering and scans this file to prove nothing is painted outside it.
 *
 * Stable, so items standing on the same row keep the order they were given; the caller puts the
 * boat last, so a tie draws the boat in front. Sorting a tie by anything else would make the
 * picture depend on map iteration order.
 */
export function paintOrder<T extends DepthItem>(items: readonly T[]): T[] {
  return [...items].sort((a, b) => a.feetPx - b.feetPx);
}
